import { useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import LinearProgress from '@mui/material/LinearProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import AddIcon from '@mui/icons-material/Add'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { useClippingsQuery } from './useClippingsQuery'

/**
 * Port of `app/clipping/clippings-list.html` +
 * `app/clipping/clippingsListController.js` (AngularJS
 * `clippingsListController`) — the "Consulta de publicações" screen, reached
 * at `/workspace/clippings-list` (legacy state `workspace.clippingsList`,
 * see `AppRoutes.jsx`).
 *
 * Legacy behavior ported 1:1:
 * - A single "email" filter plus a dynamic list of "clipping code" filters
 *   (`clippingCodes`, starts with one empty row, "+" button appends another
 *   — `addClippingCode()`). Both are sent to `ClippingsListService.getClippings`
 *   on "Buscar".
 * - Validation before submitting: `if(!$scope.email && !clippingCodes) ...`.
 *   Note this is the exact (buggy) legacy condition — `clippingCodes` is
 *   always a *truthy* array (even `[undefined]`, since `$scope.clippingCodes`
 *   starts as `[{}]` and `.map` always returns an array), so in practice the
 *   `!clippingCodes` half of the `||` never fires and this guard only ever
 *   blocks submission when `email` is falsy. Ported as-is rather than
 *   "fixed", since correcting it is a product decision out of this task's
 *   scope (documented here per the task instructions instead of guessing).
 * - Response formatting (`_formatClipping`): `clippingDate`/`releaseDate`
 *   reformatted from `YYYY-MM-DD` to `DD/MM/YYYY`; `whenReject`/`createdAt`
 *   reformatted to `DD/MM/YYYY HH:mm`; `status` mapped to a `statusText`
 *   label (`Recebida`/`Tratada`/`Rejeitada`).
 * - Table columns: Id publicação, Disponibilização (`releaseDate`),
 *   Publicação (`clippingDate`), Recebimento (`createdAt`), Nº Processo
 *   (`lawsuitNumber`), Status (+ extra per-status detail line), and a
 *   "visualizar" action button per row.
 * - "Visualizar" (`viewDetails`) opens a modal showing `JSON.stringify(clipping)`
 *   of the raw row — ported here as a MUI `Dialog` (`Materialize`'s
 *   `.modal`/`.modal-overlay` has no other equivalent already in use
 *   elsewhere in the React app; `Dialog` is the standard MUI primitive for
 *   this).
 * - Empty-state row ("Nenhuma publicação encontrada...") shown only after a
 *   search has been submitted (`submitted`) and returned zero results.
 *
 * Deliberately NOT ported:
 * - `ng-init="init()"` on the root `<div>`: `clippingsListController.js` has
 *   no `init` function defined on `$scope` at all (confirmed by reading the
 *   full controller) — calling it is a silent AngularJS no-op in the legacy
 *   app, i.e. dead markup, not a feature.
 * - `ng-init="page=1"` and the `full`/`inline-flex` Materialize layout
 *   classes: `page` is never read anywhere else in the controller or
 *   template (no pagination is actually implemented server- or
 *   client-side), so it is dead state, not a pagination feature to
 *   reproduce. Per `plan.md`'s explicit note that swapping Materialize for
 *   MUI is a design decision (not 1:1), layout uses MUI's `Stack`/`Grid`
 *   primitives instead.
 * - The `$last`-only tooltip on the "+" button: ported as a plain icon
 *   button (MUI's tooltip primitive would be the equivalent, but the
 *   original tooltip text — "Adicionar código" — is preserved via the
 *   button's `aria-label` instead for accessibility, since introducing a
 *   `Tooltip` wrapper per task scope is a minor visual nicety, not core
 *   behavior).
 */

const CLIPPING_STATUS_LABELS = {
  REJECTED: 'Rejeitada',
  ASSIGNED: 'Tratada',
  RECEIVED: 'Recebida',
}

function formatDateOnly(value) {
  if (!value) return ''
  // Legacy: `moment(clippingDate, 'YYYY-MM-DD').format('DD/MM/YYYY')`.
  const [year, month, day] = String(value).split('-')
  if (!year || !month || !day) return value
  return `${day}/${month}/${year}`
}

function formatDateTime(value) {
  if (!value) return ''
  // Legacy: `moment(whenReject/createdAt).format('DD/MM/YYYY HH:mm')`.
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  const pad = (n) => String(n).padStart(2, '0')
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function formatClipping(clipping) {
  return {
    ...clipping,
    clippingDate: formatDateOnly(clipping.clippingDate),
    releaseDate: formatDateOnly(clipping.releaseDate),
    whenReject: formatDateTime(clipping.whenReject),
    createdAt: formatDateTime(clipping.createdAt),
    statusText: CLIPPING_STATUS_LABELS[clipping.status],
  }
}

// `prop-types` isn't a project dependency (see the same note in
// `RoutePlaceholder.jsx`/`AppProviders.jsx`), so the `react/prop-types` lint
// rule is disabled here rather than pulling in a new dependency for this
// small internal (non-exported) presentational component.
/* eslint-disable react/prop-types */
function ClippingStatusCell({ clipping }) {
  return (
    <TableCell>
      {clipping.statusText}
      {clipping.status === 'REJECTED' && (
        <>
          <br />
          <Typography variant="caption" component="span" sx={{ display: 'block' }}>
            Quem rejeitou: {clipping.whoRejectedLogin}
          </Typography>
          <Typography variant="caption" component="span" sx={{ display: 'block' }}>
            em {clipping.whenReject}
          </Typography>
        </>
      )}
      {clipping.status === 'ASSIGNED' && (
        <>
          <br />
          <Typography variant="caption" component="span" sx={{ display: 'block' }}>
            Quem tratou: {clipping.whoAssignedLogin}
          </Typography>
        </>
      )}
      {clipping.status === 'RECEIVED' && (
        <>
          <br />
          <Typography variant="caption" component="span" sx={{ display: 'block' }}>
            Aguardando Tratamento
          </Typography>
        </>
      )}
    </TableCell>
  )
}
/* eslint-enable react/prop-types */

function ClippingsListPage() {
  const { getClippings, isLoading } = useClippingsQuery()

  const nextClippingCodeId = useRef(1)
  const [email, setEmail] = useState('')
  const [clippingCodes, setClippingCodes] = useState([{ id: 0, code: '' }])
  const [clippings, setClippings] = useState([])
  const [submitted, setSubmitted] = useState(false)
  const [details, setDetails] = useState(null)
  const [snackbar, setSnackbar] = useState({ open: false, message: '' })

  function handleClippingCodeChange(index, value) {
    setClippingCodes((current) =>
      current.map((entry, i) => (i === index ? { ...entry, code: value } : entry)),
    )
  }

  function handleAddClippingCode() {
    setClippingCodes((current) => [...current, { id: nextClippingCodeId.current++, code: '' }])
  }

  async function handleGetClippings() {
    const codes = clippingCodes.map((entry) => entry.code)

    // Ported 1:1 from the legacy guard — see doc-comment above for why the
    // `!codes` half never actually fires.
    if (!email && !codes) {
      setSnackbar({ open: true, message: 'Preencha os campos da busca' })
      return
    }

    setSubmitted(true)
    try {
      const data = await getClippings({ email, clippingCodes: codes })
      setClippings(data.map(formatClipping))
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'Erro na requisição' })
    }
  }

  function handleViewDetails(clipping) {
    setDetails(JSON.stringify(clipping))
  }

  function handleCancelDetails() {
    setDetails(null)
  }

  function handleSnackbarClose() {
    setSnackbar((current) => ({ ...current, open: false }))
  }

  const showEmptyState = clippings.length === 0 && submitted && !isLoading

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Consulta de publicações
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }} variant="outlined">
        <Stack spacing={2}>
          <TextField
            label="Digite o email do usuário"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            fullWidth
          />

          <Stack spacing={1}>
            {clippingCodes.map((clippingCode, index) => (
              <Stack direction="row" spacing={1} key={clippingCode.id} sx={{ alignItems: 'center' }}>
                <TextField
                  label="Digite o código da publicação"
                  value={clippingCode.code}
                  onChange={(event) => handleClippingCodeChange(index, event.target.value)}
                  fullWidth
                />
                {index === clippingCodes.length - 1 && (
                  <IconButton
                    color="primary"
                    aria-label="Adicionar código"
                    onClick={handleAddClippingCode}
                  >
                    <AddIcon />
                  </IconButton>
                )}
              </Stack>
            ))}
          </Stack>

          <Box>
            <Button variant="contained" onClick={handleGetClippings} disabled={isLoading}>
              Buscar
            </Button>
          </Box>
        </Stack>
      </Paper>

      {clippings.length > 0 && (
        <Typography variant="body2" sx={{ mb: 1 }}>
          Mostrando: {clippings.length}
        </Typography>
      )}

      {isLoading && <LinearProgress sx={{ mb: 2 }} />}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Id publicação</TableCell>
              <TableCell>Disponibilização</TableCell>
              <TableCell>Publicação</TableCell>
              <TableCell>Recebimento</TableCell>
              <TableCell>Nº Processo</TableCell>
              <TableCell>Status</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {!isLoading &&
              clippings.map((clipping, index) => (
                <TableRow key={clipping.codPublicacao ?? index}>
                  <TableCell>{clipping.codPublicacao}</TableCell>
                  <TableCell>{clipping.releaseDate}</TableCell>
                  <TableCell>{clipping.clippingDate}</TableCell>
                  <TableCell>{clipping.createdAt}</TableCell>
                  <TableCell>{clipping.lawsuitNumber}</TableCell>
                  <ClippingStatusCell clipping={clipping} />
                  <TableCell>
                    <IconButton
                      color="primary"
                      aria-label="Visualizar"
                      onClick={() => handleViewDetails(clipping)}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            {showEmptyState && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="caption">Nenhuma publicação encontrada</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={details !== null} onClose={handleCancelDetails} maxWidth="sm" fullWidth>
        <DialogTitle>Dados da publicação</DialogTitle>
        <DialogContent>
          <Typography sx={{ wordBreak: 'break-word' }}>{details}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDetails}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity="error" variant="filled" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default ClippingsListPage
